<template>
  <div class="ai-assistant-panel">
    <h3 class="panel-title">{{ panelTitle }} (AI)</h3>

    <div class="panel-body">
      <div class="scrollable-content">
        <!-- Executive Summary -->
        <div v-if="executiveSummary" class="executive-summary" v-loading="isLoading">
          <div class="summary-header">
            <svg class="summary-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M20 9V7c0-1.1-.9-2-2-2h-3c0-1.66-1.34-3-3-3S9 3.34 9 5H6c-1.1 0-2 .9-2 2v2c-1.66 0-3 1.34-3 3s1.34 3 3 3v4c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2v-4c1.66 0 3-1.34 3-3s-1.34-3-3-3zM7.5 11.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5S9.83 13 9 13s-1.5-.67-1.5-1.5zM16 17H8v-1c0-1.33 2.67-2 4-2s4 .67 4 2v1zm-1-4c-.83 0-1.5-.67-1.5-1.5S14.17 10 15 10s1.5.67 1.5 1.5S15.83 13 15 13z" fill="currentColor"/>
            </svg>
            <span class="summary-title">Executive Summary</span>
          </div>
          <p class="summary-text">{{ executiveSummary }}</p>
        </div>

        <!-- Key Metrics Section - Always show when metrics are available -->
        <div v-if="keyMetricsInsights?.length" class="key-metrics-section">
          <div class="metrics-header" @click="toggleMetrics">
            <span class="metrics-title">Key Metrics</span>
            <button class="collapse-button" :class="{ expanded: metricsExpanded }">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7 10L12 15L17 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
          <div v-if="metricsExpanded" class="metrics-content">
            <div v-for="(item, idx) in keyMetricsInsights" :key="idx" class="metric-item">
              <div class="metric-header">
                <span class="metric-name">{{ item.metric_display_name || item }}</span>
                <PinButton
                  :isPinned="false"
                  :metricId="item.metric_id"
                  :disabled="isPinLimitReached"
                  :disabledTooltip="'Maximum 3 metrics can be pinned. Unpin a metric from Top KPIs to pin this one.'"
                  @toggle="handleTogglePin"
                />
              </div>
              <div class="metric-value-row">
                <span class="metric-value">{{ item.value_display || '—' }}</span>
                <div 
                  v-if="item && !item.percent_change_is_na" 
                  class="metric-change" 
                  :class="getTrendClass(item)"
                >
                  <svg v-if="item.direction == 'up'" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M16 6L18.29 8.29L13.41 13.17L9.41 9.17L2 16.59L3.41 18L9.41 12L13.41 16L19.71 9.71L22 12V6H16Z" fill="#25B8C2"/>
                  </svg>
                  <svg v-else-if="item.direction == 'down'" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M16 18L18.29 15.71L13.41 10.83L9.41 14.83L2 7.41L3.41 6L9.41 12L13.41 8L19.71 14.29L22 12V18H16Z" fill="#D10000"/>
                  </svg>
                  <svg v-else-if="item.direction == 'neutral'" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M21.5 12L17.5 8V11H2.5V13H17.5V16L21.5 12Z" fill="#E1A325"/>
                  </svg>
                  <span class="change-text">{{ item.percent_change_display }}</span>
                </div>
              </div>
              <span class="metric-helper-text">{{ item.helper_text || 'vs. prev ' + timeRangeLabel }}</span>
            </div>
          </div>
        </div>

        <div v-if="!insightsData && !keyMetricsInsights?.length && !isLoading" class="insights-empty">
          <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="#D1D5DB">
            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/>
          </svg>
          <p>Click to generate AI insights for the current view.</p>
          <Button
            color-type="dark-blue"
            @click="fetchInsights"
          >
            Generate Insights
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import Button from '@/NewLibrary/Button.vue';
import PinButton from '../PinButton.vue';

const store = useStore();

const isLoading = computed(() => store.state.myPractice.clinicDashboard.aiInsightsLoading);
const insightsData = computed(() => store.state.myPractice.clinicDashboard.aiInsights);
const viewRole = computed(() => store.state.myPractice.clinicDashboard.dashboardFilters.viewRole);
const timeRangeMap = computed(() => store.state.myPractice.clinicDashboard.timeRangeMap || {});
const timeRangeLabel = computed(() => timeRangeMap.value[store.state.myPractice.clinicDashboard.dashboardFilters.timeRange] || 'Custom Range');

const panelTitle = computed(() =>
  viewRole.value === 'Admin' ? 'Practice View Insights' : 'Provider View Insights'
);

const executiveSummary = computed(() => {
  const summary = insightsData.value?.insights?.find(item => item.insight_type === 'executive_summary')?.content || '';
  return summary;
});

const keyMetricsInsights = computed(() => {
  const metricIds = store.getters['myPractice/clinicDashboard/keyMetrics'];
  if (!metricIds || metricIds.length === 0) return [];
  
  return metricIds.map(metricId => {
    const metric = store.getters['myPractice/clinicDashboard/getMetricById'](metricId);
    if (!metric) return null;
    
    return metric;
  }).filter(Boolean);
});

const isPinLimitReached = computed(() => {
  const pinnedMetricIds = store.getters['myPractice/clinicDashboard/topKpis'];
  return pinnedMetricIds.length >= 3;
});

const metricsExpanded = ref(false);

function fetchInsights() {
  store.dispatch('myPractice/clinicDashboard/fetchAIInsightsData');
}

function toggleMetrics() {
  metricsExpanded.value = !metricsExpanded.value;
}

function getTrendClass(metric) {
  if (!metric) return '';
  switch (metric.direction) {
    case 'up':
      return 'trend-up';
    case 'down':
      return 'trend-down';
    case 'neutral':
      return 'trend-neutral';
    default:
      return '';
  }
}

function handleTogglePin(metricId) {
  if (!metricId) return;
  store.dispatch('myPractice/clinicDashboard/toggleMetricPin', metricId);
}
</script>

<style scoped>
.ai-assistant-panel {
  background: #F9FBFD;
  border-radius: 10px;
  box-shadow: 0px 1px 15px 0px rgba(164, 164, 164, 0.25);
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.panel-title {
  font-family: 'Inter', sans-serif;
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  color: #333;
  margin: 0;
  padding: 12px 16px 6px 16px;
  flex-shrink: 0;
}

.panel-body {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  gap: 12px;
  padding: 0 16px 16px 16px;
}

.scrollable-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 0;
}

.insights-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.executive-summary {
  background: #E4F3F5;
  border-radius: 5px;
  box-shadow: 0px 1px 15px 0px rgba(164, 164, 164, 0.25);
  padding: 8px 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.summary-header {
  display: flex;
  align-items: center;
  gap: 6px;
}

.summary-icon {
  width: 20px;
  height: 20px;
  color: #20A7C1;
  flex-shrink: 0;
}

.summary-title {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: #20A7C1;
}

.summary-text {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 300;
  line-height: 18px;
  color: #333;
  margin: 0;
}

.key-metrics-section {
  border-bottom: 1px solid #C4C4C4;
  padding-bottom: 4px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-height: 0;
}

.metrics-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  cursor: pointer;
  user-select: none;
}

.metrics-title {
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.15px;
  color: #333;
  list-style: disc;
  display: list-item;
  margin-left: 24px;
}

.collapse-button {
  width: 24px;
  height: 24px;
  border-radius: 12px;
  background: #F9FBFD;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s;
  padding: 0;
  color: #333;
  flex-shrink: 0;
}

.collapse-button.expanded {
  transform: rotate(180deg);
}

.metrics-content {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex:1;
  min-height: 0;
  overflow-y: auto;
}

.metric-item {
  background: #fff;
  border-radius: 5px;
  padding: 4px 12px 6px 12px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.metric-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 5px;
}

.metric-name {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.4px;
  color: #666;
  list-style: disc;
  display: list-item;
  margin-left: 18px;
  flex: 1;
}


.metric-value-row {
  display: flex;
  align-items: flex-end;
  gap: 8px;
}

.metric-value {
  font-family: 'Inter', sans-serif;
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  color: #374957;
}

.metric-change {
  display: flex;
  align-items: center;
  gap: 4px;
}
.metric-change svg {
  width: 18px;
  height: 18px;
}

.change-text {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.4px;
}
.metric-change.trend-neutral .change-text {
  color: #E1A325;
}
.metric-change.trend-up .change-text {
  color: #25B8C2;
}
.metric-change.trend-down .change-text {
  color: #D10000;
}

.metric-helper-text {
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  font-weight: 400;
  line-height: 12px;
  letter-spacing: 0.2px;
  color: #999;
}

.insights-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
  text-align: center;
}

.insights-empty p {
  font-size: 13px;
  color: #9CA3AF;
  margin: 12px 0;
}

.panel-input {
  flex-shrink: 0;
  height: 86px;
}

.input-box {
  background: #fff;
  border: 1px solid #C4C4C4;
  border-radius: 8px;
  padding: 12px;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 10px;
  height: 100%;
}

.input-placeholder {
  flex: 1;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: #999;
  align-self: flex-start;
}

.send-button {
  width: 24px;
  height: 24px;
  background: #F2F2F2;
  border: none;
  border-radius: 2px;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #666;
}

.send-button:hover {
  background: #E8E8E8;
}
</style>
